import { createFileRoute, Outlet, useMatch } from "@tanstack/react-router";
import NavBar from "@/components/nav/NavBar";
import { MapProvider } from "@/lib/map/MapProvider";
import BaseMap from "@/lib/map/BaseMap";
import useLayout from "@/lib/layout/useLayout";
import useIsMobile from "@/lib/utils/useIsMobile";
import {
    MouseEventHandler,
    TouchEventHandler,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { debounce } from "lodash";
import MobileNavBar from "@/components/nav/MobileNavBar";
import { Divider, Skeleton } from "@heroui/react";
import FieldDashboardProvider from "@/lib/fieldDashboard/FieldDashboardProvider";
import useAuth from "@/lib/auth/useAuth";

export const Route = createFileRoute("/dashboard")({
    component: RouteComponent,
});

function RouteComponent() {
    const { user } = useAuth();
    const { layoutState, setLayoutState } = useLayout();
    const { isMobile, height, width } = useIsMobile();
    const headerOffset = 64;
    const navOffset = 56;
    const match = useMatch({ from: "/dashboard/", shouldThrow: false });

    const [currentY, setCurrentY] = useState(0);
    const [currentX, setCurrentX] = useState(0);
    const isDragging = useRef(false);
    const drawerRef = useRef<HTMLDivElement>(null);

    const topTenth = height - (height * 9) / 10;
    const rightTenth = (width * 9) / 10;
    const drawerBuffer = 32; // h-8, size of the bottom tab

    const minWidthWhenVisible = 600;

    const handleStart = (y: number, x: number) => {
        isDragging.current = true;
        setCurrentY(y);
        setCurrentX(x);

        if (drawerRef.current) {
            drawerRef.current.style.transition = "none";
        }
    };

    const handleMove = useCallback((clientY: number, clientX: number) => {
        if (!isDragging.current) return;

        setCurrentY(clientY);
        setCurrentX(clientX);
    }, []);

    const handleEnd = useCallback(() => {
        if (!isDragging.current) return;
        isDragging.current = false;

        if (drawerRef.current) {
            drawerRef.current.style.transition = isMobile
                ? "height 0.3s ease"
                : "width 0.3s ease";
        }

        if (isMobile) {
            if (currentY < topTenth) {
                setLayoutState("maximized");
                setCurrentY(height - headerOffset);
            } else if (currentY > height - drawerBuffer) {
                setLayoutState("minimized");
                setCurrentY(height - drawerBuffer - drawerBuffer);
            } else {
                setLayoutState("flex");
            }
        } else {
            if (currentX > rightTenth) {
                setLayoutState("maximized");
                setCurrentX(width - navOffset);
            } else if (
                currentX > (minWidthWhenVisible * 1) / 3 + navOffset &&
                currentX <= minWidthWhenVisible + navOffset
            ) {
                setLayoutState("flex");
                setCurrentX(minWidthWhenVisible + navOffset);
            } else if (
                currentX <=
                navOffset + drawerBuffer + minWidthWhenVisible / 3
            ) {
                setLayoutState("minimized");
                setCurrentX(navOffset);
            } else {
                setLayoutState("flex");
            }
        }
    }, [
        currentX,
        currentY,
        height,
        isMobile,
        rightTenth,
        setLayoutState,
        topTenth,
        width,
    ]);

    const handleTouchStart: TouchEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        handleStart(e.touches[0].clientY, e.touches[0].clientX);
    };

    const handleTouchMove: TouchEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        handleMove(e.touches[0].clientY, e.touches[0].clientX);
    };

    const handleMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
        e.preventDefault();
        handleStart(e.clientY, e.clientX);
    };

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (isDragging.current) {
                handleMove(e.clientY, e.clientX);
                e.preventDefault();
            }
        },
        [handleMove],
    );

    const handleMouseUp = useCallback(() => {
        handleEnd();
    }, [handleEnd]);

    useEffect(() => {
        const move = debounce((e) => {
            handleMouseMove(e);
        }, 1);
        const up = debounce(() => {
            handleMouseUp();
        }, 1);
        if (isDragging.current) {
            window.addEventListener("mousemove", move, { passive: false });
            window.addEventListener("mouseup", up);
        }

        return () => {
            window.removeEventListener("mousemove", move);
            window.removeEventListener("mouseup", up);
        };
    }, [handleMouseMove, handleMouseUp]);

    const containerWidth = useMemo(() => {
        const draggableWdith = `${(currentX / width) * 100 - (navOffset / width) * 100 + (drawerBuffer / width) * 50}vw`;

        if (isMobile) return "100vw";
        if (isDragging.current) return draggableWdith;

        if (match || layoutState == "none") return "0";

        if (layoutState == "full_screen") return `calc(100vw - ${navOffset}px)`;
        if (layoutState == "maximized") return `calc(100vw - ${navOffset}px)`;

        if (layoutState == "flex") return draggableWdith;

        if (layoutState == "standard")
            return `${minWidthWhenVisible + navOffset}px`;

        return "32px";
    }, [currentX, isMobile, layoutState, match, width]);

    const containerHeight = useMemo(() => {
        const draggableHeight = `${100 - (currentY / height) * 100 + (drawerBuffer / height) * 50}dvh`;
        if (!isMobile) return `100dvh`;
        if (isDragging.current) return draggableHeight;

        if (match || layoutState == "none") return "0";
        if (layoutState == "maximized") return `calc(100dvh - ${navOffset}px)`;
        if (layoutState == "full_screen")
            return `calc(100dvh - ${navOffset}px)`;
        if (layoutState == "flex") return draggableHeight;
        if (layoutState == "standard") return "50dvh";
        return "64px";
    }, [currentY, height, isMobile, layoutState, match]);

    if (!user) {
        return (
            <div className="flex w-screen h-screen">
                <Skeleton className="w-full h-full" />
            </div>
        );
    }

    return (
        <div className="md:grid md:grid-cols-[auto_1fr] grid grid-rows-[auto_1fr] h-[100dvh] pt-safe-bottom w-screen overflow-hidden overscroll-contain">
            {isMobile ? <MobileNavBar /> : <NavBar />}
            <div className="md:h-screen h-full w-screen md:w-full relative overscroll-contain">
                <MapProvider>
                    <FieldDashboardProvider>
                        <div className="flex flex-col-reverse md:flex-row w-full h-full touch-none z-[5]">
                            <div
                                style={{
                                    width: containerWidth,
                                    height: containerHeight,
                                }}
                                ref={drawerRef}
                                className="flex flex-col md:flex-row-reverse"
                            >
                                {layoutState != "full_screen" &&
                                    containerWidth != "0" && (
                                        <div
                                            className="bg-white min-h-8 md:h-full md:w-8 md:min-w-8 w-full flex justify-center hover:cursor-row-resize hover:md:cursor-col-resize  z-[6]  items-center cursor-pointer"
                                            onMouseDown={handleMouseDown}
                                            onTouchStart={handleTouchStart}
                                            onTouchMove={handleTouchMove}
                                            onTouchEnd={handleEnd}
                                        >
                                            <div className="rounded-full bg-gray-300 h-3 w-24 md:h-24 md:w-3"></div>
                                        </div>
                                    )}
                                <Divider
                                    orientation={
                                        isMobile ? "horizontal" : "vertical"
                                    }
                                    className="bg-gray-300 z-[6]"
                                />
                                <div className="flex-1 overflow-y-scroll scrollbar-hide select-none bg-white z-[6] ">
                                    <Outlet />
                                </div>
                            </div>
                            <div
                                className="flex-1 bg-blue-300 md:z-[5]"
                                style={{
                                    width: isMobile
                                        ? "100%"
                                        : layoutState != "full_screen"
                                          ? "0"
                                          : "100%",
                                    height: isMobile
                                        ? layoutState != "full_screen"
                                            ? "0"
                                            : "calc(100% - 56px)"
                                        : "100%",
                                }}
                            >
                                <BaseMap />
                            </div>
                        </div>
                    </FieldDashboardProvider>
                </MapProvider>
            </div>
        </div>
    );
}
